<template>
  <Layout>
    <div class="dashboard-container">
      <div class="dashboard-header">
        <h1 class="dashboard-title">{{$t('hi_dashboard')}}</h1>
        <SelectboxOutline
            :options="options"
            @update:selectedValue="$args => (programChartPeriod=$args)"
            class="row-filter"
            :selected-option="programChartPeriod"
        >
          <i class="dropdown-icon"></i>
        </SelectboxOutline>
      </div>
      <div class="main-tabs">
        <button
            class="main-tab-btn"
            :class="{ 'active': mainActiveTab === 'programs' }"
            @click="setMainActiveTab('programs')"
        >
          {{$t('program_analysis')}}
        </button>
        <button
            class="main-tab-btn"
            :class="{ 'active': mainActiveTab === 'users' }"
            @click="setMainActiveTab('users')"
        >
          {{$t('user_analysis')}}
        </button>
      </div>

      <div v-if="mainActiveTab === 'programs'" class="tab-content-wrapper">

        <div v-if="isLoading" class="tabs-wrapper">
          <div v-for="i in 3" :key="'stat-shimmer-' + i" class="tab-item shimmer-stat-card">
            <div class="tab-header">
              <free-style-shimmer
                  :is-loading="true"
                  height="16px"
                  width="120px"
                  border-radius="4px"
                  color="#EAECF0"
              />
            </div>
            <div class="tab-content">
              <div class="tab-value-wrapper">
                <free-style-shimmer
                    :is-loading="true"
                    height="32px"
                    width="60px"
                    border-radius="4px"
                    color="#EAECF0"
                />
                <free-style-shimmer
                    :is-loading="true"
                    height="24px"
                    width="50px"
                    border-radius="12px"
                    color="#EAECF0"
                    style="margin-left: 12px;"
                />
              </div>
              <div class="tab-description-row">
                <free-style-shimmer
                    :is-loading="true"
                    height="12px"
                    width="180px"
                    border-radius="4px"
                    color="#EAECF0"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="tabs-wrapper">
          <div
              class="tab-item"
              :class="{ 'active': activeTab === 'programs', 'selected': activeTab === 'programs' }"
              @click="setActiveTab('programs')"
          >
            <div class="tab-header">
              <div class="tab-title">{{$t('published_programs')}}</div>
            </div>
            <div class="tab-content">
              <div class="tab-value-wrapper">
                <div class="tab-value">{{programPublishedData.count || 0}}</div>

                <div class="tab-trend" :class="getTrendClass(programPublishedData)">
                  <svg v-if="programPublishedData.change?.isIncrease === true" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 16L9 11L13 15L20 8M20 8V14M20 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="programPublishedData.change?.isIncrease === false" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 8L9 13L13 9L20 16M20 16V10M20 16H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span v-else class="trend-neutral-icon">—</span>

                  <span>{{ formatPercentage(programPublishedData) }}</span>
                </div>
              </div>
              <div class="tab-description-row">
                <div class="tab-added">{{ periodDescriptionText }} +{{ programPublishedData.filtered_count || 0 }} {{$t('published_program_added')}}</div>
              </div>
            </div>
          </div>

          <div
              class="tab-item"
              :class="{ 'active': activeTab === 'tests', 'selected': activeTab === 'tests' }"
              @click="setActiveTab('tests')"
          >
            <div class="tab-header">
              <div class="tab-title">{{$t('draft_programs')}}</div>
            </div>
            <div class="tab-content">
              <div class="tab-value-wrapper">
                <div class="tab-value">{{programDraftData.count || 0}}</div>

                <div class="tab-trend" :class="getTrendClass(programDraftData)">
                  <svg v-if="programDraftData.change?.isIncrease === true" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 16L9 11L13 15L20 8M20 8V14M20 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="programDraftData.change?.isIncrease === false" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 8L9 13L13 9L20 16M20 16V10M20 16H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span v-else class="trend-neutral-icon">—</span>

                  <span>{{ formatPercentage(programDraftData) }}</span>
                </div>
              </div>
              <div class="tab-description-row">
                <div class="tab-added">{{ periodDescriptionText }} +{{ programDraftData.filtered_count || 0 }} {{$t('draft_program_added')}}</div>
              </div>
            </div>
          </div>

          <div
              class="tab-item"
              :class="{ 'active': activeTab === 'users', 'selected': activeTab === 'users' }"
              @click="setActiveTab('users')"
          >
            <div class="tab-header">
              <div class="tab-title">{{$t('expired_programs')}}</div>
            </div>
            <div class="tab-content">
              <div class="tab-value-wrapper">
                <div class="tab-value">{{programExpiredData.count || 0}}</div>

                <div class="tab-trend" :class="getTrendClass(programExpiredData)">
                  <svg v-if="programExpiredData.change?.isIncrease === true" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 16L9 11L13 15L20 8M20 8V14M20 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="programExpiredData.change?.isIncrease === false" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 8L9 13L13 9L20 16M20 16V10M20 16H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span v-else class="trend-neutral-icon">—</span>

                  <span>{{ formatPercentage(programExpiredData) }}</span>
                </div>
              </div>
              <div class="tab-description-row">
                <div class="tab-added">{{ periodDescriptionText }} +{{ programExpiredData.filtered_count || 0 }} {{$t('total_expired_programs')}}</div>
              </div>
            </div>
          </div>

        </div>

        <div class="center-wrapper">
          <div class="center-holder">
            <div v-if="programTableLoading" class="shimmer-table-container">
              <div class="shimmer-table-header">
                <free-style-shimmer
                    :is-loading="true"
                    height="24px"
                    width="120px"
                    border-radius="6px"
                    color="#EAECF0"
                />
              </div>
              <div class="shimmer-table-head">
                <free-style-shimmer
                    :is-loading="true"
                    height="40px"
                    width="100%"
                    border-radius="0"
                    color="#F9FAFB"
                />
              </div>
              <div class="shimmer-table-rows">
                <div v-for="i in 5" :key="i" class="shimmer-table-row">
                  <div class="shimmer-cell program-cell">
                    <free-style-shimmer
                        :is-loading="true"
                        height="48px"
                        width="48px"
                        border-radius="8px"
                        color="#EAECF0"
                    />
                    <div class="shimmer-program-info">
                      <free-style-shimmer
                          :is-loading="true"
                          height="14px"
                          width="140px"
                          border-radius="4px"
                          color="#EAECF0"
                      />
                      <free-style-shimmer
                          :is-loading="true"
                          height="12px"
                          width="100px"
                          border-radius="4px"
                          color="#EAECF0"
                      />
                    </div>
                  </div>
                  <div class="shimmer-cell">
                    <free-style-shimmer
                        :is-loading="true"
                        height="24px"
                        width="70px"
                        border-radius="8px"
                        color="#EAECF0"
                    />
                  </div>
                  <div class="shimmer-cell">
                    <free-style-shimmer
                        :is-loading="true"
                        height="14px"
                        width="60px"
                        border-radius="4px"
                        color="#EAECF0"
                    />
                  </div>
                  <div class="shimmer-cell">
                    <free-style-shimmer
                        :is-loading="true"
                        height="14px"
                        width="80px"
                        border-radius="4px"
                        color="#EAECF0"
                    />
                  </div>
                  <div class="shimmer-cell">
                    <free-style-shimmer
                        :is-loading="true"
                        height="8px"
                        width="100px"
                        border-radius="4px"
                        color="#EAECF0"
                    />
                  </div>
                </div>
              </div>
            </div>
            <ProgramTable
                v-else
                :programs="programs"
                :loading="false"
                :title="$t('programs')"
                :show-view-all="true"
                :page-size="10"
                :search-query="''"
                :status-filter="'all'"
                @program-select="handleProgramSelect"
                @view-all="handleViewAllPrograms"
                @sort-change="handleProgramSortChange"
                @funnel-modal-state-change="handleFunnelModalStateChange"
            />
          </div>
        </div>


      </div>

      <div v-if="mainActiveTab === 'users'" class="tab-content-wrapper">
        <div class="user-content-placeholder">
          <div class="tabs-wrapper">

            <div
                class="tab-item"
                :class="{ 'active': activeTab === 'programs' }"
                @click="setActiveTab('programs')"
            >
              <div class="tab-header">
                <div class="tab-title">{{$t('total_user_title')}}</div>
              </div>
              <div class="tab-content">
                <div class="tab-value-wrapper">
                  <div class="tab-value">{{usersChartData.count || 0}}</div>

                  <div class="tab-trend" :class="getTrendClass(usersChartData)">
                    <svg v-if="usersChartData.change?.isIncrease === true" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 16L9 11L13 15L20 8M20 8V14M20 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else-if="usersChartData.change?.isIncrease === false" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8L9 13L13 9L20 16M20 16V10M20 16H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span v-else class="trend-neutral-icon">—</span>

                    <span>{{ formatPercentage(usersChartData) }}</span>
                  </div>
                </div>
                <div class="tab-description-row">
                  <div class="tab-added">{{ periodDescriptionText }} +{{ usersChartData.filtered_count || 0 }} {{$t('user_added')}}</div>
                </div>
              </div>
            </div>

            <div
                class="tab-item"
                :class="{ 'active': activeTab === 'tests' }"
                @click="setActiveTab('tests')"
            >
              <div class="tab-header">
                <div class="tab-title">{{$t('at_risk_users')}}</div>
              </div>
              <div class="tab-content">
                <div class="tab-value-wrapper">
                  <div class="tab-value">{{testChartData.count || 0}}</div>
                  <img src="../assets/images/warning.svg"/>
                </div>
                <div class="tab-description-row">
                  <div class="tab-added">{{ periodDescriptionWithLast }} {{$t('no_progress_users')}}</div>
                </div>
              </div>
            </div>

            <div
                class="tab-item"
                :class="{ 'active': activeTab === 'users' }"
                @click="setActiveTab('users')"
            >
              <div class="tab-header">
                <div class="tab-title">{{$t('number_of_online_users')}}</div>
              </div>
              <div class="tab-content">
                <div class="tab-value-wrapper">
                  <div class="tab-value">{{programChartData.count || 0}}</div>
                  <img src="../assets/images/online.svg"/>
                </div>
                <div class="tab-description-row">
                  <span class="tab-added">{{$t('users_currently_online')}}</span>
                </div>
              </div>
            </div>

          </div>

          <div class="center-wrapper">
            <div class="center-holder">
              <div v-if="userTableLoading" class="shimmer-table-container">
                <div class="shimmer-table-header">
                  <free-style-shimmer
                      :is-loading="true"
                      height="24px"
                      width="120px"
                      border-radius="6px"
                      color="#EAECF0"
                  />
                </div>
                <div class="shimmer-table-head">
                  <free-style-shimmer
                      :is-loading="true"
                      height="40px"
                      width="100%"
                      border-radius="0"
                      color="#F9FAFB"
                  />
                </div>
                <div class="shimmer-table-rows">
                  <div v-for="i in 5" :key="i" class="shimmer-table-row">
                    <div class="shimmer-cell user-cell">
                      <free-style-shimmer
                          :is-loading="true"
                          height="40px"
                          width="40px"
                          border-radius="50%"
                          color="#EAECF0"
                      />
                      <div class="shimmer-user-info">
                        <free-style-shimmer
                            :is-loading="true"
                            height="14px"
                            width="120px"
                            border-radius="4px"
                            color="#EAECF0"
                        />
                        <free-style-shimmer
                            :is-loading="true"
                            height="12px"
                            width="160px"
                            border-radius="4px"
                            color="#EAECF0"
                        />
                      </div>
                    </div>
                    <div class="shimmer-cell">
                      <free-style-shimmer
                          :is-loading="true"
                          height="14px"
                          width="80px"
                          border-radius="4px"
                          color="#EAECF0"
                      />
                    </div>
                    <div class="shimmer-cell">
                      <free-style-shimmer
                          :is-loading="true"
                          height="14px"
                          width="100px"
                          border-radius="4px"
                          color="#EAECF0"
                      />
                    </div>
                    <div class="shimmer-cell">
                      <free-style-shimmer
                          :is-loading="true"
                          height="24px"
                          width="70px"
                          border-radius="8px"
                          color="#EAECF0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DashboardUserTable
                  v-else
                  :users="users"
                  :loading="false"
                  :title="$t('users')"
                  :show-view-all="true"
                  @user-select="handleUserSelect"
                  @sort-change="handleUserSortChange"
              />
            </div>
          </div>

        </div>
      </div>

      <div v-if="isLoading" class="bottom-cards">
        <div v-for="i in 3" :key="'bottom-shimmer-' + i" class="info-card">
          <div class="info-card-header">
            <free-style-shimmer
                :is-loading="true"
                height="40px"
                width="40px"
                border-radius="50%"
                color="#EAECF0"
                style="margin-right: 12px;"
            />
            <div class="info-card-meta">
              <free-style-shimmer
                  :is-loading="true"
                  height="14px"
                  width="100px"
                  border-radius="4px"
                  color="#EAECF0"
                  style="margin-bottom: 8px;"
              />
              <div style="display: flex; align-items: center; gap: 8px;">
                <free-style-shimmer
                    :is-loading="true"
                    height="18px"
                    width="40px"
                    border-radius="4px"
                    color="#EAECF0"
                />
                <free-style-shimmer
                    :is-loading="true"
                    height="20px"
                    width="50px"
                    border-radius="12px"
                    color="#EAECF0"
                />
              </div>
            </div>
            <div class="info-card-chart">
              <free-style-shimmer
                  :is-loading="true"
                  height="50px"
                  width="120px"
                  border-radius="4px"
                  color="#EAECF0"
              />
            </div>
          </div>
          <div class="info-card-body">
            <div class="info-card-subtitle">
              <free-style-shimmer
                  :is-loading="true"
                  height="14px"
                  width="180px"
                  border-radius="4px"
                  color="#EAECF0"
              />
              <free-style-shimmer
                  :is-loading="true"
                  height="14px"
                  width="60px"
                  border-radius="4px"
                  color="#EAECF0"
              />
            </div>
            <ul class="shimmer-list">
              <li v-for="j in 3" :key="'item-shimmer-' + j" class="shimmer-list-item">
                <free-style-shimmer
                    :is-loading="true"
                    height="40px"
                    width="40px"
                    border-radius="50%"
                    color="#EAECF0"
                />
                <div class="shimmer-list-info">
                  <free-style-shimmer
                      :is-loading="true"
                      height="14px"
                      width="120px"
                      border-radius="4px"
                      color="#EAECF0"
                  />
                  <free-style-shimmer
                      :is-loading="true"
                      height="12px"
                      width="80px"
                      border-radius="4px"
                      color="#EAECF0"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-else class="bottom-cards">
        <div class="info-card">
          <div class="info-card-header">
            <div class="info-card-icon users">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.60509 3.74338L12.0004 2.09998L19.3957 3.74338C19.5955 3.7878 19.7743 3.89904 19.9024 4.05874C20.0305 4.21844 20.1004 4.41704 20.1004 4.62178V13.6101C20.1003 14.499 19.8808 15.3742 19.4613 16.158C19.0418 16.9417 18.4353 17.6098 17.6956 18.1029L12.0004 21.9L6.30519 18.1029C5.56564 17.6099 4.95921 16.942 4.5397 16.1584C4.1202 15.3748 3.9006 14.4998 3.90039 13.611V4.62178C3.90043 4.41704 3.97026 4.21844 4.09838 4.05874C4.2265 3.89904 4.40523 3.7878 4.60509 3.74338ZM5.70039 5.34358V13.6101C5.7004 14.2027 5.84671 14.7861 6.12633 15.3086C6.40594 15.8311 6.81023 16.2765 7.30329 16.6053L12.0004 19.7373L16.6975 16.6053C17.1904 16.2766 17.5946 15.8314 17.8742 15.309C18.1539 14.7867 18.3002 14.2034 18.3004 13.611V5.34358L12.0004 3.94498L5.70039 5.34358ZM12.0004 11.1C11.4037 11.1 10.8314 10.8629 10.4094 10.441C9.98744 10.019 9.75039 9.44671 9.75039 8.84998C9.75039 8.25324 9.98744 7.68094 10.4094 7.25899C10.8314 6.83703 11.4037 6.59998 12.0004 6.59998C12.5971 6.59998 13.1694 6.83703 13.5914 7.25899C14.0133 7.68094 14.2504 8.25324 14.2504 8.84998C14.2504 9.44671 14.0133 10.019 13.5914 10.441C13.1694 10.8629 12.5971 11.1 12.0004 11.1ZM7.97469 15.6C8.08379 14.6085 8.55491 13.6922 9.29776 13.0266C10.0406 12.361 11.003 11.993 12.0004 11.993C12.9978 11.993 13.9602 12.361 14.703 13.0266C15.4459 13.6922 15.917 14.6085 16.0261 15.6H7.97469Z" fill="#525866"/>
              </svg>
            </div>
            <div class="info-card-meta">
              <div class="info-card-title">{{$t('total_experts')}}</div>
              <div class="info-card-stats">
                <div class="info-card-value">{{expertChartData.count || 0}}</div>

                <div class="info-card-trend" :class="getTrendClass(expertChartData)">
                  <template v-if="expertChartData.change?.isIncrease === true">+</template>
                  <template v-else-if="expertChartData.change?.isIncrease === false">-</template>
                  {{ Math.abs(expertChartData.change?.percentage || 0) }}%
                </div>
              </div>
            </div>

            <div class="info-card-chart">
              <div class="expert-card-chart"></div>
            </div>
          </div>
          <div class="info-card-body">
            <div class="info-card-subtitle">
              <span>{{$t('most_active_five_experts')}}</span>
              <a href="experts">{{$t('see_all_title')}}</a>
            </div>
            <ul class="experts-list">
              <li
                  v-for="expert in expertChartData.latest || []"
                  :key="expert.id"
                  class="expert-item"
                  @click="handleExpertClick(expert)"
              >
                <div class="expert-avatar">
                  <img :src="expert.avatarUrl || ''" :alt="expert.name || ''" onerror="this.src='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='">
                </div>
                <div class="expert-details">
                  <span class="expert-span">{{expert.name}} {{expert.surname}}</span>
                  <div class="expert-name">{{ expert.name || $t('unknown') }}</div>
                </div>

                <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="cursor: pointer;"
                    @click.stop="handleExpertClick(expert)"
                >
                  <path d="M3.79648 4.99956L0.0839844 1.28706L1.14448 0.226562L5.91748 4.99956L1.14448 9.77256L0.0839844 8.71206L3.79648 4.99956Z" fill="#525866"/>
                </svg>

                <div class="expert-arrow"></div>
              </li>
              <li v-if="!expertChartData.latest || expertChartData.latest.length === 0" class="expert-item">
                <div class="expert-details">
                  <div class="expert-name">{{$t('no_experts_to_display')}}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <div class="info-card-icon tests">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.7225 8.39996L6.457 7.74296C6.4127 7.63334 6.39044 7.51607 6.39148 7.39785C6.39253 7.27963 6.41685 7.16277 6.46307 7.05395C6.50929 6.94513 6.5765 6.84648 6.66085 6.76364C6.74521 6.6808 6.84506 6.6154 6.95469 6.57116L15.2995 3.19976C15.5207 3.11054 15.7682 3.11274 15.9877 3.20589C16.2073 3.29904 16.3809 3.47552 16.4704 3.69656L21.5284 16.2138C21.5727 16.3234 21.5949 16.4406 21.5939 16.5589C21.5929 16.6771 21.5685 16.794 21.5223 16.9028C21.4761 17.0116 21.4089 17.1102 21.3245 17.1931C21.2402 17.2759 21.1403 17.3413 21.0307 17.3856L12.6859 20.757C12.5763 20.8013 12.459 20.8235 12.3408 20.8225C12.2226 20.8214 12.1057 20.7971 11.9969 20.7509C11.8881 20.7047 11.7894 20.6375 11.7066 20.5531C11.6237 20.4688 11.5583 20.3689 11.5141 20.2593L11.4349 20.064V20.1H7.8349C7.5962 20.1 7.36728 20.0051 7.1985 19.8364C7.02972 19.6676 6.9349 19.4387 6.9349 19.2V18.957L3.91989 17.7393C3.69863 17.6497 3.52198 17.476 3.42882 17.2562C3.33565 17.0365 3.33359 16.7887 3.42309 16.5675L6.7225 8.39996ZM8.73489 18.3H10.723L8.73489 13.3797V18.3ZM6.9349 12.6804L5.4292 16.4073L6.9349 17.0472V12.6804ZM8.46309 7.90226L12.8461 18.7509L19.5223 16.0536L15.1393 5.20586L8.46309 7.90226ZM11.1433 9.73196C10.9219 9.82135 10.6741 9.81913 10.4543 9.7258C10.2346 9.63246 10.0609 9.45565 9.97149 9.23426C9.8821 9.01287 9.88432 8.76504 9.97766 8.54528C10.071 8.32553 10.2478 8.15185 10.4692 8.06246C10.6906 7.97295 10.9385 7.97505 11.1583 8.0683C11.3781 8.16156 11.5519 8.33832 11.6414 8.55971C11.731 8.7811 11.7289 9.02898 11.6356 9.24882C11.5423 9.46866 11.3656 9.64245 11.1442 9.73196H11.1433Z" fill="#525866"/>
              </svg>
            </div>
            <div class="info-card-meta">
              <div class="info-card-title">{{$t('total_visual_test')}}</div>
              <div class="info-card-stats">
                <div class="info-card-value">{{gameLabChartData.count || 0}}</div>
                <div class="info-card-trend" :class="getTrendClass(gameLabChartData)">
                  <template v-if="gameLabChartData.change?.isIncrease === true">+</template>
                  <template v-else-if="gameLabChartData.change?.isIncrease === false">-</template>
                  {{ Math.abs(gameLabChartData.change?.percentage || 0) }}%
                </div>
              </div>
            </div>

            <div class="info-card-chart">
              <div class="visual-test-card-chart"></div>
            </div>

          </div>
          <div class="info-card-body">
            <div class="info-card-subtitle">
              <span>{{$t('most_recent_five_visual_tests')}}</span>
              <a href="game-labs">{{$t('see_all_title')}}</a>
            </div>
            <ul class="tests-list">
              <li
                  v-for="test in gameLabChartData.latest || []"
                  :key="test.id"
                  class="test-item"
                  @click="handleGameLabClick(test)"
              >
                <div class="test-icon">
                  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.14648 6.875C8.14648 6.18464 7.58684 5.625 6.89648 5.625C6.20613 5.625 5.64648 6.18464 5.64648 6.875C5.64648 7.56536 6.20613 8.125 6.89648 8.125C7.58684 8.125 8.14648 7.56536 8.14648 6.875ZM9.39648 6.875C9.39648 8.25571 8.2772 9.375 6.89648 9.375C5.51577 9.375 4.39648 8.25571 4.39648 6.875C4.39648 5.49429 5.51577 4.375 6.89648 4.375C8.2772 4.375 9.39648 5.49429 9.39648 6.875ZM15.959 5H10.959V6.25H15.959V5ZM15.959 9.375H10.959V10.625H15.959V9.375ZM15.959 13.75H10.959V15H15.959V13.75ZM6.89648 14.375C6.20613 14.375 5.64648 13.8154 5.64648 13.125C5.64648 12.4346 6.20613 11.875 6.89648 11.875C7.58684 11.875 8.14648 12.4346 8.14648 13.125C8.14648 13.8154 7.58684 14.375 6.89648 14.375ZM6.89648 15.625C8.2772 15.625 9.39648 14.5057 9.39648 13.125C9.39648 11.7443 8.2772 10.625 6.89648 10.625C5.51577 10.625 4.39648 11.7443 4.39648 13.125C4.39648 14.5057 5.51577 15.625 6.89648 15.625ZM6.89648 7.5C7.24166 7.5 7.52148 7.22018 7.52148 6.875C7.52148 6.52982 7.24166 6.25 6.89648 6.25C6.55131 6.25 6.27148 6.52982 6.27148 6.875C6.27148 7.22018 6.55131 7.5 6.89648 7.5Z" fill="#525866"/>
                  </svg>
                </div>
                <div class="test-details">
                  <div class="test-name">{{ test.name || $t('untitled_test') }}</div>
                  <div class="test-date">{{ (test.createdAt) || $t('no_date') }}</div>
                </div>

                <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="cursor: pointer;"
                    @click.stop="handleGameLabClick(test)"
                >
                  <path d="M3.79648 4.99956L0.0839844 1.28706L1.14448 0.226562L5.91748 4.99956L1.14448 9.77256L0.0839844 8.71206L3.79648 4.99956Z" fill="#525866"/>
                </svg>

                <div class="test-arrow"></div>
              </li>
              <li v-if="!gameLabChartData.latest || gameLabChartData.latest.length === 0" class="test-item">
                <div class="test-details">
                  <div class="test-name">{{$t('no_visual_tests_to_display')}}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <div class="info-card-icon blogs">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.7664 14.7L15.3664 20.0964L5.46821 20.1C5.23014 20.101 5.00144 20.0074 4.83234 19.8398C4.66325 19.6722 4.5676 19.4444 4.56641 19.2063V4.79372C4.56641 4.30052 4.96691 3.90002 5.46011 3.90002H19.8727C20.3659 3.90002 20.7664 4.31042 20.7664 4.80182V14.7ZM18.9664 5.70002H6.36641V18.3H13.5664V13.8C13.5664 13.5796 13.6474 13.3668 13.7938 13.2021C13.9403 13.0374 14.1422 12.9321 14.3611 12.9063L14.4664 12.9L18.9664 12.8991V5.70002ZM18.2203 14.6991L15.3664 14.7V17.5521L18.2203 14.6991Z" fill="#525866"/>
              </svg>
            </div>
            <div class="info-card-meta">
              <div class="info-card-title">{{$t('total_blogs')}}</div>
              <div class="info-card-stats">
                <div class="info-card-value">{{blogChartData.count || 0}}</div>
                <div class="info-card-trend" :class="getTrendClass(blogChartData)">
                  <template v-if="blogChartData.change?.isIncrease === true">+</template>
                  <template v-else-if="blogChartData.change?.isIncrease === false">-</template>
                  {{ Math.abs(blogChartData.change?.percentage || 0) }}%
                </div>
              </div>
            </div>

            <div class="info-card-chart">
              <div class="blog-card-chart"></div>
            </div>

          </div>
          <div class="info-card-body">
            <div class="info-card-subtitle">
              <span>{{$t('most_recent_five_blogs')}}</span>
              <a href="blogs">{{$t('see_all_title')}}</a>
            </div>
            <ul class="blogs-list">
              <li
                  v-for="blog in blogChartData.latest || []"
                  :key="blog.id"
                  class="blog-item"
                  @click="handleBlogClick(blog)"
              >
                <div class="blog-image">
                  <div class="blog-avatar">
                    <img v-if="blog.avatarUrl" :src="blog.avatarUrl || ''" :alt="blog.name || ''" onerror="this.src='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='">
                    <img v-else src="../assets/images/ibility-default.jpeg">
                  </div>
                </div>
                <div class="blog-details">
                  <div class="blog-title">{{ blog.name || $t('untitled_blog') }}</div>
                  <div class="blog-date">
                    <img src="@/assets/images/calendarGray.svg" />
                    <span>{{ (blog.createdAt) || $t('no_date') }}</span>
                  </div>
                </div>

                <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="cursor: pointer;"
                    @click.stop="handleBlogClick(blog)"
                >
                  <path d="M3.79648 4.99956L0.0839844 1.28706L1.14448 0.226562L5.91748 4.99956L1.14448 9.77256L0.0839844 8.71206L3.79648 4.99956Z" fill="#525866"/>
                </svg>

                <div class="blog-arrow"></div>
              </li>
              <li v-if="!blogChartData.latest || blogChartData.latest.length === 0" class="blog-item">
                <div class="blog-details">
                  <div class="blog-title">{{$t('no_blogs_to_display')}}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>



    </div>
  </Layout>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue';
import ApexCharts from 'apexcharts';
import Layout from "@/components/layouts/Layout.vue";
import dashboardService from "@/service/dashboardService";
import { handleErrorResponse} from "@/utils/utils";
import SelectboxOutline from "@/components/SelectboxOutline.vue";
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ProgramTable from "@/components/tables/ProgramTable.vue";
import DashboardUserTable from "@/components/tables/DashboardUserTable.vue";

const { t } = useI18n();
const router = useRouter();

const mainActiveTab = ref('programs');
const activeTab = ref('programs');
const programChartPeriod = ref('month');

const isLoading = ref(true);
const programTableLoading = ref(false);
const userTableLoading = ref(false);

const isFunnelModalOpen = ref(false)

const currentProgramFilter = ref('all');
const currentUserFilter = ref('all');

const options = ref([
  { value: 'week', text: t('last_week') },
  { value: 'month', text: t('last_month') },
  { value: 'year', text: t('last_year') },
  { value: 'all', text: t('all_time') },
]);

const usersChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const programChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const testChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const programPublishedData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const programDraftData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const programExpiredData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null }
});

const expertChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null },
  latest: []
});

const gameLabChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null },
  latest: []
});

const blogChartData = ref({
  count: 0,
  filtered_count: 0,
  percentage: 0,
  change: { count: 0, percentage: 0, isIncrease: null },
  latest: []
});

const programs = ref([]);
const users = ref([]);

let expertChartInstance = null;
let visualTestChartInstance = null;
let blogChartInstance = null;

const getTrendClass = (data) => {
  if (data?.change?.isIncrease === true) return 'positive';
  if (data?.change?.isIncrease === false) return 'negative';
  return 'neutral';
};

const formatPercentage = (data) => {
  if (data?.change?.isIncrease === null) return '0%';
  const percentage = Math.abs(data?.percentage || 0);
  const sign = data?.change?.isIncrease ? '+' : '';
  return `${sign}${percentage}%`;
};

const getChartColor = (chartData) => {
  if (chartData?.change?.isIncrease === true) return "#385dfb";
  if (chartData?.change?.isIncrease === false) return "#ef4444";
  return "#94a3b8";
};

const getChartData = (chartData) => {
  if (chartData?.change?.isIncrease === null) {
    return [15, 15, 15, 15, 15, 15, 15, 15, 15, 15];
  }
  return [10, 15, 8, 20, 12, 18, 15, 22, 16, 14];
};

const setMainActiveTab = (tab) => {
  if (!tab || typeof tab !== 'string') {
    return;
  }

  mainActiveTab.value = tab;

  if (tab === 'programs') {
    activeTab.value = 'programs';
  } else if (tab === 'users') {
    activeTab.value = 'programs';
  }

  nextTick(() => {
    setTimeout(() => {
      initCardCharts();
    }, 200);
  });
};

const periodDescriptionText = computed(() => {
  const periodMap = {
    'all': t('for_all_time'),
    'week': t('this_week'),
    'month': t('this_month'),
    'year': t('this_year')
  };
  return periodMap[programChartPeriod.value] || t('this_month');
});

const periodDescriptionWithLast = computed(() => {
  const periodMap = {
    'all': t('for_all_time'),
    'week': t('for_the_last_week'),
    'month': t('for_the_last_month'),
    'year': t('for_the_last_year')
  };
  return periodMap[programChartPeriod.value] || t('for_the_last_month');
});

const setActiveTab = (tab) => {
  if (!tab || typeof tab !== 'string') {
    return;
  }

  activeTab.value = tab;

  if (mainActiveTab.value === 'programs') {
    let filter = 'all';

    if (tab === 'programs') {
      filter = 'published';
    } else if (tab === 'tests') {
      filter = 'draft';
    } else if (tab === 'users') {
      filter = 'expired';
    }

    currentProgramFilter.value = filter;

    const mappedPeriod = dashboardService.mapPeriod(programChartPeriod.value);
    loadProgramsData(mappedPeriod, filter);
  } else if (mainActiveTab.value === 'users') {
    let filter = 'all';

    if (tab === 'programs') {
      filter = 'all';
    } else if (tab === 'tests') {
      filter = 'risk';
    } else if (tab === 'users') {
      filter = 'active';
    }

    currentUserFilter.value = filter;

    const mappedPeriod = dashboardService.mapPeriod(programChartPeriod.value);
    loadUsersData(mappedPeriod, filter);
  }
};

const handleExpertClick = (expert) => {
  if (!expert?.id) {
    return
  }
  router.push(`/experts/detail/${expert.id}`)
}

const handleBlogClick = (blog) => {
  if (!blog?.id) {
    return
  }
  router.push(`/blog/${blog.id}`)
}

const handleGameLabClick = (gameLab) => {
  if (!gameLab?.id) {
    return
  }
  router.push(`/game/${gameLab.id}`)
}

const parseDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return new Date();
  }

  const parts = dateString.split('.');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }

  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const mapProgramStatus = (status) => {
  if (!status || typeof status !== 'string') {
    return 'draft';
  }

  const statusMap = {
    'Yayında': 'published',
    'yayında': 'published',
    'Taslak': 'draft',
    'taslak': 'draft',
    'Arşiv': 'archived',
    'arşiv': 'archived'
  };

  return statusMap[status] || 'draft';
};

const mapProgramData = (programsResponse) => {
  if (!programsResponse?.data?.data) {
    return;
  }

  const data = programsResponse.data.data;
  const summary = data?.summary;

  if (!summary) {
    return;
  }

  programPublishedData.value = {
    count: summary.published?.totalCount ?? 0,
    filtered_count: summary.published?.addedInPeriod ?? 0,
    percentage: Math.abs(summary.published?.changePercentage ?? 0),
    change: {
      count: summary.published?.change ?? 0,
      percentage: Math.abs(summary.published?.changePercentage ?? 0),
      isIncrease: summary.published?.isIncrease ?? null
    }
  };

  programDraftData.value = {
    count: summary.draft?.totalCount ?? 0,
    filtered_count: summary.draft?.addedInPeriod ?? 0,
    percentage: Math.abs(summary.draft?.changePercentage ?? 0),
    change: {
      count: summary.draft?.change ?? 0,
      percentage: Math.abs(summary.draft?.changePercentage ?? 0),
      isIncrease: summary.draft?.isIncrease ?? null
    }
  };

  programExpiredData.value = {
    count: summary.expired?.totalCount ?? 0,
    filtered_count: summary.expired?.expiredInPeriod ?? 0,
    percentage: Math.abs(summary.expired?.changePercentage ?? 0),
    change: {
      count: summary.expired?.change ?? 0,
      percentage: Math.abs(summary.expired?.changePercentage ?? 0),
      isIncrease: summary.expired?.isIncrease ?? null
    }
  };

  programs.value = Array.isArray(data.recentPrograms)
      ? data.recentPrograms.map(program => ({
        id: program?.programId ?? null,
        title: program?.name ?? 'Untitled Program',
        subtitle: program?.name ?? '',
        description: program?.name ?? '',
        image: program?.avatar || null,
        status: mapProgramStatus(program?.status),
        openCount: program?.journeyCount ?? 0,
        assignedCount: program?.assignedUserCount ?? 0,
        completionRate: program?.completionPercentage ?? 0,
        publishDate: program?.startAt ? parseDate(program.startAt) : new Date(),
        publishEndDate: program?.startAt ? parseDate(program.finishAt) : new Date(),
        moduleProgress: Array.isArray(program?.moduleProgress) ? program.moduleProgress : [],
        updatedAt: program?.updatedAt ? parseDate(program.updatedAt) : new Date()
      }))
      : [];
};

const mapUserData = (usersResponse) => {
  if (!usersResponse?.data?.data) {
    return;
  }

  const data = usersResponse.data.data;
  const summary = data?.summary;

  if (!summary) {
    return;
  }

  usersChartData.value = {
    count: summary.totalUsers?.totalCount ?? 0,
    filtered_count: summary.totalUsers?.addedInPeriod ?? 0,
    percentage: Math.abs(summary.totalUsers?.changePercentage ?? 0),
    change: {
      count: summary.totalUsers?.change ?? 0,
      percentage: Math.abs(summary.totalUsers?.changePercentage ?? 0),
      isIncrease: summary.totalUsers?.isIncrease ?? null
    }
  };

  testChartData.value = {
    count: summary.riskUsers?.totalCount ?? 0,
    filtered_count: 0,
    percentage: 0,
    change: { count: 0, percentage: 0, isIncrease: null }
  };

  programChartData.value = {
    count: summary.activeUsers?.totalCount ?? 0,
    filtered_count: 0,
    percentage: 0,
    change: { count: 0, percentage: 0, isIncrease: null }
  };

  users.value = Array.isArray(data.usersList)
      ? data.usersList.map(user => ({
        id: user?.userId ?? null,
        name: `${user?.name ?? ''} ${user?.surname ?? ''}`.trim() || 'Unknown User',
        department: user?.department ?? "Unknown",
        avatar: user?.avatar || null,
        totalPoints: user?.leaderboardScore ?? 0,
        status: user?.status === "Aktif" ? "aktif" : "pasif",
        isRisk: user?.isRisk ?? false,
        leaderboardRank: user?.leaderboardRank ?? 0,
        activeDuration: 0,
        assignedPrograms: user?.assignedPrograms ?? 0,
        completionRate: parseFloat(String(user?.completionRate ?? 0).replace('%', '')) || 0
      }))
      : [];
};

const mapExpertData = (expertsResponse) => {
  if (!expertsResponse?.data?.data) {
    return;
  }

  const data = expertsResponse.data.data;
  const summary = data?.summary;

  if (!summary) {
    return;
  }

  expertChartData.value = {
    count: summary.totalCount ?? 0,
    filtered_count: summary.addedInPeriod ?? 0,
    percentage: Math.abs(summary.changePercentage ?? 0),
    change: {
      count: summary.change ?? 0,
      percentage: Math.abs(summary.changePercentage ?? 0),
      isIncrease: summary.isIncrease ?? null
    },
    latest: Array.isArray(data.topExperts)
        ? data.topExperts.slice(0, 3).map(expert => ({
          id: expert?.expertId ?? null,
          name: expert?.name ?? 'Unknown Expert',
          surname: expert?.surname ?? '',
          avatarUrl: expert?.avatar || null,
          totalSlots: expert?.totalSlots ?? 0
        }))
        : []
  };
};

const mapGameLabData = (gameLabsResponse) => {
  if (!gameLabsResponse?.data?.data) {
    return;
  }

  const data = gameLabsResponse.data.data;
  const summary = data?.summary;

  if (!summary) {
    return;
  }

  gameLabChartData.value = {
    count: summary.totalCount ?? 0,
    filtered_count: summary.addedInPeriod ?? 0,
    percentage: Math.abs(summary.changePercentage ?? 0),
    change: {
      count: summary.change ?? 0,
      percentage: Math.abs(summary.changePercentage ?? 0),
      isIncrease: summary.isIncrease ?? null
    },
    latest: Array.isArray(data.recentGameLabs)
        ? data.recentGameLabs.slice(0, 3).map(test => ({
          id: test?.gameLabsId ?? null,
          name: test?.name ?? 'Untitled Test',
          createdAt: test?.createdAt ?? null,
          status: test?.status ?? 'Draft',
          avatarUrl: test?.avatarUrl || null
        }))
        : []
  };
};

const mapBlogData = (blogsResponse) => {
  if (!blogsResponse?.data?.data) {
    return;
  }

  const data = blogsResponse.data.data;
  const summary = data?.summary;

  if (!summary) {
    return;
  }

  blogChartData.value = {
    count: summary.totalCount ?? 0,
    filtered_count: summary.addedInPeriod ?? 0,
    percentage: Math.abs(summary.changePercentage ?? 0),
    change: {
      count: summary.change ?? 0,
      percentage: Math.abs(summary.changePercentage ?? 0),
      isIncrease: summary.isIncrease ?? null
    },
    latest: Array.isArray(data.recentBlogs)
        ? data.recentBlogs.slice(0, 3).map(blog => ({
          id: blog?.blogId ?? null,
          name: blog?.name ?? 'Untitled Blog',
          description: blog?.description ?? 'No description',
          createdAt: blog?.createdAt ?? null,
          type: blog?.type ?? '1',
          avatarUrl: blog?.avatarUrl || null
        }))
        : []
  };
};

const loadProgramsData = async (period = 'lastMonth', filter = 'all') => {
  try {
    programTableLoading.value = true;
    const result = await dashboardService.getProgramsData(period, filter);
    if (result) {
      mapProgramData(result);
    }
  } catch (error) {
    handleErrorResponse(error);
  } finally {
    programTableLoading.value = false;
  }
};

const loadUsersData = async (period = 'lastMonth', filter = 'all') => {
  try {
    userTableLoading.value = true;
    const result = await dashboardService.getUsersData(period, filter);
    if (result) {
      mapUserData(result);
    }
  } catch (error) {
    handleErrorResponse(error);
  } finally {
    userTableLoading.value = false;
  }
};

const loadDashboardData = async (period = 'lastMonth') => {
  if (!period || typeof period !== 'string') {
    return;
  }

  isLoading.value = true;

  try {
    await loadProgramsData(period, currentProgramFilter.value);

    await loadUsersData(period, currentUserFilter.value);

    const [expertsResult, gameLabsResult, blogsResult] = await Promise.allSettled([
      dashboardService.getExpertsData(period),
      dashboardService.getGameLabsData(period),
      dashboardService.getBlogsData(period)
    ]);

    if (expertsResult.status === 'fulfilled' && expertsResult.value) {
      mapExpertData(expertsResult.value);
    }

    if (gameLabsResult.status === 'fulfilled' && gameLabsResult.value) {
      mapGameLabData(gameLabsResult.value);
    }

    if (blogsResult.status === 'fulfilled' && blogsResult.value) {
      mapBlogData(blogsResult.value);
    }

  } catch (error) {
    handleErrorResponse(error);
  } finally {
    isLoading.value = false;
  }
};

watch(programChartPeriod, (newPeriod) => {
  if (!newPeriod) {
    return;
  }

  currentProgramFilter.value = 'all';
  const mappedPeriod = dashboardService.mapPeriod(newPeriod);
  if (mappedPeriod) {
    loadDashboardData(mappedPeriod);
  }
});

const handleProgramSelect = (program) => {
  if (!program?.id) {
    return;
  }
  router.push(`/programs/${program.id}`);
};

const handleViewAllPrograms = () => {
  router.push('/programs');
};

const handleProgramSortChange = ({ field, order } = {}) => {
  if (!field || !order) {
    return;
  }
};

const handleUserSelect = (user) => {
  if (!user?.id) {
    return;
  }
  router.push(`/users/${user.id}`);
};

const handleUserSortChange = ({ field, order } = {}) => {
  if (!field || !order) {
    return;
  }
};

const handleFunnelModalStateChange = (isOpen) => {
  isFunnelModalOpen.value = isOpen;
};

const initCardCharts = () => {
  try {
    const createChartOptions = (chartData, color) => ({
      series: [{
        name: "Data",
        data: getChartData(chartData)
      }],
      chart: {
        type: "line",
        height: 50,
        width: 120,
        sparkline: { enabled: true },
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false }
      },
      stroke: {
        curve: chartData?.change?.isIncrease === null ? "straight" : "smooth",
        width: 3,
        colors: [color]
      },
      tooltip: {
        enabled: chartData?.change?.isIncrease !== null,
        custom: function({ series, seriesIndex, dataPointIndex }) {
          const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
          return `<div style="background: #14213d; color: white; padding: 8px 12px; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.2); min-width: 50px; text-align: center;">
            <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: ${color}; margin-right: 4px;"></span>
              <span style="font-size: 14px;">${value}</span>
            </div>
          </div>`;
        },
        fixed: { enabled: false },
        marker: { show: true }
      },
      grid: { show: false }
    });

    const expertOptions = createChartOptions(expertChartData.value, getChartColor(expertChartData.value));
    const visualTestOptions = createChartOptions(gameLabChartData.value, getChartColor(gameLabChartData.value));
    const blogOptions = createChartOptions(blogChartData.value, getChartColor(blogChartData.value));

    const renderCharts = (selector, options, instanceRef) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element, index) => {
        if (!element) return;
        if (element._chartInstance) {
          element._chartInstance.destroy();
        }
        const chart = new ApexCharts(element, options);
        chart.render();
        element._chartInstance = chart;
        if (index === 0) instanceRef = chart;
      });
    };

    renderCharts(".expert-card-chart", expertOptions, expertChartInstance);
    renderCharts(".visual-test-card-chart", visualTestOptions, visualTestChartInstance);
    renderCharts(".blog-card-chart", blogOptions, blogChartInstance);

  } catch (error) {
  }
};

onMounted(async () => {
  try {
    await loadDashboardData('lastMonth');

    setTimeout(() => {
      initCardCharts();
    }, 150);
  } catch (error) {
  }
});

onUnmounted(() => {
  try {
    if (expertChartInstance) expertChartInstance.destroy();
    if (visualTestChartInstance) visualTestChartInstance.destroy();
    if (blogChartInstance) blogChartInstance.destroy();
  } catch (error) {
  }
});

</script>

<style scoped>

.tab-trend.neutral {
  background-color: #f3f4f6;
  color: #6b7280;
}

.info-card-trend.neutral {
  color: #6b7280;
  background-color: #f3f4f6;
  font-size: 12px;
}

.trend-neutral-icon {
  font-size: 15px;
  font-weight: bold;
  color: currentColor;
}

.dashboard-container {
  max-width: 1450px;
  margin: 0 auto;
  padding: 0px;
  font-family: euclid_regular, 'sans-serif';
  color: #101828;
  background-color: #fcfcfd;
}

.dashboard-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
}

.dashboard-title {
  font-size: 28px;
  color: #101828;
  margin-bottom: 16px;
  font-family: 'euclid_semi_bold', 'sans-serif';
}

.tabs-wrapper {
  display: flex;
  background-color: transparent;
  overflow: hidden;
  border-radius: 12px;
  max-height: 139px;
  gap: 12px;
  margin-bottom: 15px;
}

.tab-item {
  flex: 1;
  position: relative;
  cursor: pointer;
  padding-top: 4px;
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.tab-item:hover {
  box-shadow: 0 4px 12px rgba(67, 103, 255, 0.15);
}

.tab-item.active,
.tab-item.selected {
  background-color: #f5f5ff;
  box-shadow: 0 4px 12px rgba(67, 103, 255, 0.15);
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 0px 16px;
}

.tab-title {
  font-size: 16px;
  font-weight: 500;
  color: #101828;
}

.dropdown span{
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #667085;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  margin-left: 4px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.tab-content {
  padding: 0 16px 16px 16px;
}

.tab-value-wrapper {
  display: flex;
  align-items: center;
}

.tab-value {
  font-size: 28px;
  font-weight: 600;
  color: #101828;
  margin-right: 12px;
}

.tab-trend {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 3px 8px;
  gap: 5px;
  font-size: 12px;
  font-weight: bold;
}

.tab-trend.positive {
  background-color: #e6f4ea;
  color: #0f5132;
}

.tab-trend.negative {
  background-color: #feeceb;
  color: #b02a37;
}

.trend-icon {
  color: currentColor;
  margin-right: 4px;
}

.tab-description-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 12px;
}

.tab-added {
  font-size: 12px;
  color: #667085;
  text-align: left;
}

.bottom-cards {
  display: flex;
  gap: 20px;
  margin-top: 15px;
}

.center-wrapper{
  display: flex;
  width: 100%;
}

.center-holder{
  display: flex;
  width: 100%;
  background-color: transparent;
  border-radius: 12px;
}

.info-card {
  flex: 1;
  background-color: #FFFFFF;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 375px;
}

.info-card-header {
  display: flex;
  align-items: center;
  padding: 30px 16px;
  background-color: transparent;
  border: 1px solid #EAECF0;
  position: relative;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  height: 120px;
}

.info-card-chart {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 120px;
  height: 50px;
}

.expert-card-chart, .visual-test-card-chart, .blog-card-chart {
  width: 100%;
  height: 100%;
}

.info-card-icon {
  display: flex;
  width: 40px;
  height: 40px;
  margin-right: 12px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px;
  border: 1px solid #E2E4E9;
  border-radius: 50%;
}

.info-card-icon.users {
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-card-icon.tests {
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-card-icon.blogs {
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-card-meta {
  flex: 1;
  margin-right: 120px;
}

.info-card-title {
  font-size: 14px;
  color: #101828;
  margin-right: auto;
  text-align: left;
}

.info-card-stats {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.info-card-value {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  margin-right: 8px;
}

.info-card-trend {
  font-size: 14px;
  padding: 2px 12px;
  border-radius: 32px;
  font-weight: 600;
}

.info-card-trend.positive {
  color: #186448;
  background-color: #cbf5e5;
  font-size: 12px;
}

.info-card-trend.negative {
  color: #B02A37;
  background-color: #fadedc;
  font-size: 12px;
}

.info-card-body {
  padding: 16px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  border-color: #EAECF0;
  border-style: solid;
  border-left-width: 1px;
  border-right-width: 1px;
  border-bottom-width: 1px;
  border-top-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(450px - 120px);
  overflow: hidden;
}

.info-card-subtitle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.info-card-subtitle span {
  font-size: 14px;
  font-weight: 500;
  color: #101828;
}

.info-card-subtitle a {
  font-size: 13px;
  color: #4367FF;
  text-decoration: none;
  font-weight: 500;
}

.experts-list, .tests-list, .blogs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.expert-item, .test-item, .blog-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  min-height: 60px;
}

.expert-item:last-child, .test-item:last-child, .blog-item:last-child {
  border-bottom: none;
}

.expert-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 12px;
  background-color: #F2F4F7;
}

.expert-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.expert-details, .test-details, .blog-details {
  flex: 1;
  text-align: left;
}

.expert-name, .test-name, .blog-title {
  font-size: 12px;
  font-weight: 500;
  color: #667085;
  margin-bottom: 2px;
}

.test-name{
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  margin-bottom: 2px;
}

.blog-title {
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  margin-bottom: 2px;
}

.expert-span{
  color: #000000;
  font-size: 14px;
}

.expert-role, .test-date {
  font-size: 12px;
  color: #667085;
}

.expert-arrow, .test-arrow, .blog-arrow {
  width: 16px;
  height: 16px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.test-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-color: #F9FAFB;
  margin-right: 12px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 16px;
  justify-content: center;
  align-items: center;
  display: flex;
}

.blog-image {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 12px;
  background-color: transparent;
}

.blog-avatar{
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 1px solid #EAECF0;
}

.blog-image img {
  width: 40px;
  height: 40px;
  object-fit: cover;
}

.blog-description {
  font-size: 12px;
  color: #667085;
  margin: 4px 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.blog-date {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #667085;
  gap: 5px;
}

.blog-date img {
  width: 16px;
  height: 16px;
}

.main-tabs {
  display: flex;
  margin-bottom: 16px;
  gap: 0;
}

.main-tab-btn {
  background: none;
  border: none;
  padding: 12px 0;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: euclid_medium, sans-serif;
  position: relative;
  border-bottom: 2px solid transparent;
}

.main-tab-btn:hover {
  color: #344054;
}

.main-tab-btn.active {
  color: #101828;
  font-weight: 600;
  border-bottom-color: #4367FF;
}

.main-tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #4367FF;
  border-radius: 1px 1px 0 0;
}

.tab-content-wrapper {
  display: block;
}

.user-content-placeholder {
  text-align: center;
  width: 100%;
}

.user-content-placeholder h3 {
  font-size: 20px;
  color: #101828;
  margin-bottom: 8px;
  font-family: euclid_semi_bold, sans-serif;
}

.user-content-placeholder p {
  font-size: 16px;
  color: #667085;
  margin: 0;
}

@media (max-width: 1024px) {
  .bottom-cards {
    flex-direction: column;
  }

  .info-card {
    height: 400px;
  }

  .info-card-header {
    height: 110px;
  }

  .info-card-body {
    height: calc(400px - 110px);
  }
}

@media (max-width: 768px) {
  .tabs-wrapper {
    flex-direction: column;
    gap: 10px;
    background-color: transparent;
    box-shadow: none;
  }

  .tab-item {
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
    border-radius: 4px !important;
  }

  .bottom-cards {
    flex-direction: column;
  }

  .info-card {
    height: 350px;
  }

  .info-card-header {
    height: 100px;
  }

  .info-card-body {
    height: calc(350px - 100px);
  }

  .main-tabs {
    flex-direction: column;
    width: 100%;
    gap: 2px;
  }

  .main-tab-btn {
    width: 100%;
    text-align: center;
  }
}

.shimmer-table-container {
  background: white;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  overflow: hidden;
  padding: 16px;
  width: 100%;
}

.shimmer-table-header {
  padding: 0 10px;
  margin-bottom: 16px;
}

.shimmer-table-head {
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
}

.shimmer-table-rows {
  display: flex;
  flex-direction: column;
}

.shimmer-table-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #F2F4F7;
  gap: 24px;
}

.shimmer-table-row:last-child {
  border-bottom: none;
}

.shimmer-cell {
  display: flex;
  align-items: center;
}

.shimmer-cell.user-cell {
  flex: 2;
  gap: 12px;
}

.shimmer-cell:not(.user-cell) {
  flex: 1;
}

.shimmer-user-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shimmer-cell.program-cell {
  flex: 2;
  gap: 12px;
}

.shimmer-program-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shimmer-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.shimmer-list-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  gap: 12px;
}

.shimmer-list-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shimmer-stat-card {
  cursor: default;
}
</style>
